import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { ICEPACK_STATE_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	getIcepackStateInfoRequest,
	createIcepackStateRequest,
	updateIcepackStateRequest,
	deleteIcepackStateRequest,
} from '../../api/requests';
import {
	GET_ICEPACK_STATE_INFO,
	setIcepackStateInfoAction,
	setIsIcepackStateInfoLoadingAction,
	CREATE_ICEPACK_STATE,
	setIsIcepackStateCreatingAction,
	UPDATE_ICEPACK_STATE,
	setIsIcepackStateUpdatingAction,
	DELETE_ICEPACK_STATE,
	setIsIcepackStateDeletingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { getIcepacksStatesAction } from '../icepacks-states/actions';
import { transformToValidationErrors } from './utils';

export function* getIcepackStateInfoSaga({ payload: id }) {
	try {
		yield put(setIsIcepackStateInfoLoadingAction(true));

		const { data, message, toast } = yield call(getIcepackStateInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setIcepackStateInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackStateInfoLoadingAction(false));
	}
}

export function* createIcepackStateSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackStateCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createIcepackStateRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();

			yield put(getIcepacksStatesAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_STATE_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_STATE_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackStateCreatingAction(false));
	}
}

export function* updateIcepackStateSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackStateUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateIcepackStateRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setIcepackStateInfoAction(data));

			redirect();

			yield put(getIcepacksStatesAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_STATE_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_STATE_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackStateUpdatingAction(false));
	}
}

export function* deleteIcepackStateSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackStateDeletingAction(true));

		const { data, message, toast } = yield call(deleteIcepackStateRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			yield put(getIcepacksStatesAction({ searchQuery, noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackStateDeletingAction(false));
	}
}

export default function* icepackStateModalSaga() {
	yield takeEvery(GET_ICEPACK_STATE_INFO, getIcepackStateInfoSaga);
	yield takeEvery(CREATE_ICEPACK_STATE, createIcepackStateSaga);
	yield takeEvery(UPDATE_ICEPACK_STATE, updateIcepackStateSaga);
	yield takeEvery(DELETE_ICEPACK_STATE, deleteIcepackStateSaga);
}
